import React, { useCallback, useEffect, useState } from "react";
import { Col, Divider, Modal, Space, Spin, Form, Collapse } from "antd";
import CustomButton from "components/Common/buttons/CustomButton";
import { makeStyles } from "@material-ui/core/styles";
import { customDateFormatWithoutTime, textFormatter } from "components/Common/formatter";
import TitleWithDivider from "components/Common/components/TitleWithDivider";
import moment from "moment";
import { fetchCustomerById, setCustomerAvailabilityV2 } from "utils/api";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import { CLASSIFICATION } from "pages/operations/routineDetails/constants";
import TaskItem from "pages/operations/routineDetails/TaskItem";
import { formatPhoneNumber } from "utils/helpers/functions";
import classnames from "classnames";
import CustomLink from "components/Common/buttons/CustomLink";
import CalendarPicker from "pages/customer-management/customerDetails/CalendarPicker";

const { Panel } = Collapse;

const useStyles = makeStyles({
  titleWithDescription: {
    display: "flex",
    textAlign: "left",
    justifyContent: "space-between",
  },
  container: {
    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
    borderLeft: "none",
    borderRight: "none",
    marginBottom: 30,
    marginTop: "-30px",
    paddingBottom: 20,
  },
  nameContainer: {
    display: "flex",
    fontWeight: 600,
  },
  modal: {
    maxWidth: "900px!important",
  },
  phones: {
    display: "flex",
    flexWrap: "wrap",
  },
  marginL24: {
    marginLeft: 24,
  },
  marginB10: {
    marginBottom: 10,
  },
  id: {
    marginLeft: 10,
    color: "gray",
  },
  textRed: {
    color: "red",
  },
  cookInfo: {
    marginRight: 5,
    marginTop: 5,
    display: "flex",
    "&>span": {
      fontWeight: 300,
      marginRight: 5,
    },
  },
  formItem: {
    marginInline: 10,
  },
  subtitle: {
    fontWeight: 300,
    fontSize: 12,
    color: "grey",
    marginRight: 10,
    textTransform: "uppercase",
  },
  noInfo: {
    textAlign: "center",
  },
  calendarContainer: {
    maxWidth: 800,
    padding: 10,
  },
  widerCalendar: {
    gap: "16px!important",
  },
  notReachableBtn: {
    border: "2px solid #FF2E00",
    color: "#FF2E00",
  },
});

const DetailsModal = ({ customer, onClose, isOpen, updateRoutine }) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const [isLoading, setLoader] = useState(false);
  const [phones, setPhones] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const isNotReachable = customer.classification?.includes(CLASSIFICATION.NOT_REACHABLE);

  const onSubmit = useCallback(
    e => {
      setLoader(true);
      const type = e.target.dataset.type;
      const { availableAt, timeSlot } = selectedDate || {};

      const data = {
        unreachable: type === "notReachable",
        availableAt,
        timeSlot,
      };
      setCustomerAvailabilityV2(customer.id, data)
        .then(() => {
          setLoader(false);
          updateRoutine();
          onClose();
          successMessage("The customer availability has been set successfully");
        })
        .catch(err => {
          setLoader(false);
          defaultNgiErrorMethod(err);
        });
    },
    [selectedDate]
  );

  const retrieveCustomer = useCallback(() => {
    setLoader(true);
    fetchCustomerById(customer.id)
      .then(({ data }) => {
        const phones = [
          { name: "Primary Phone", value: data.result?.phone },
          { name: "Secondary Phone", value: data.result?.secondaryPhone },
          { name: "Close Relative Phone", value: data.result?.closeRelativePhone },
        ];
        const transactionNumbers = data.result?.payeePhoneNumbers?.map(number => ({
          name: "Transaction",
          value: number,
        }));
        !!transactionNumbers?.length && phones.push(...transactionNumbers);

        setPhones(phones.filter(item => item.value));
        setLoader(false);
      })
      .catch(() => {
        setLoader(false);
      });
  }, []);

  useEffect(() => {
    retrieveCustomer();
    if (customer) {
      form.setFieldsValue({
        ...(customer.availableFrom && { timeRange: [moment(customer.availableFrom), moment(customer.availableTo)] }),
        date: moment(customer.availableFrom || undefined),
      });
    }
  }, [customer]);

  return (
    <Modal
      className={classes.modal}
      title={
        <div>
          <div className={classes.titleWithDescription}>
            <div className={classes.nameContainer}>
              {`${customer.name ?? "-"}`}
              <span className={classes.id}>
                (ID{" "}
                <CustomLink underlined target="_blank" to={`/customers/customer/${customer.id}`}>
                  {customer.id}
                </CustomLink>
                )
              </span>
            </div>
            <span>
              AVAILABILITY{" "}
              {isNotReachable ? (
                <span className={classes.textRed}>NOT REACHABLE</span>
              ) : (
                <span>
                  {!!customer.availableAt
                    ? `${moment(customer.availableAt).format("DD MMM")} - ${customer.timeSlot}`
                    : "TIME SLOT NOT SET"}
                </span>
              )}
            </span>
          </div>
          <div className={classes.cookInfo}>
            <div className={classes.cookInfo}>
              <span>LAST COOKED:</span>{" "}
              <b>{customer.lastCookedAt ? customDateFormatWithoutTime(customer.lastCookedAt) : textFormatter()}</b>
            </div>
            <div className={classes.cookInfo}>
              <span>DAYS SINCE LAST COOKING:</span> <b>{textFormatter(customer.daysSinceLastCooking)}</b>
            </div>
            <div className={classes.cookInfo}>
              <span>DAYS LEFT TO NO COOK:</span> <b>{textFormatter(customer.daysToNoCook)}</b>
            </div>
          </div>
          <Divider />
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      closable={false}
      footer={[]}
    >
      <Spin spinning={isLoading}>
        <div className={classes.container}>
          <TitleWithDivider title="Schedule Availability" />
          <div className={classes.calendarContainer}>
            <CalendarPicker
              className={classes.widerCalendar}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
            />
            <br />
            <Space>
              <CustomButton
                className={classes.notReachableBtn}
                onClick={onSubmit}
                data-type="notReachable"
                type="primary"
                size="small"
                color="outlined"
                text="NOT REACHABLE"
              />

              <CustomButton
                onClick={onSubmit}
                disabled={!selectedDate}
                data-type="schedule"
                htmlType="submit"
                type="primary"
                text="SCHEDULE"
                size="small"
              />
            </Space>
          </div>

          <TitleWithDivider title="Customer Phone Numbers" />
          {phones.length ? (
            <Collapse ghost collapsible="icon">
              <Panel
                key={"phones"}
                showArrow={phones.length > 4}
                header={
                  <div className={classes.phones}>
                    {phones.map(
                      (phone, i) =>
                        i < 4 &&
                        phone.value && (
                          <Col span={6} key={phone.value}>
                            <div className={classes.nameContainer}>{formatPhoneNumber(String(phone.value))}</div>
                            <div className={classes.subtitle}>{phone.name}</div>
                          </Col>
                        )
                    )}
                  </div>
                }
              >
                <div className={classnames(classes.phones, classes.marginL24)}>
                  {phones.map(
                    (phone, i) =>
                      i >= 4 &&
                      phone.value && (
                        <Col span={6} key={phone.value} className={classes.marginB10}>
                          <div className={classes.nameContainer}>{formatPhoneNumber(String(phone.value))}</div>
                          <div className={classes.subtitle}>{phone.name}</div>
                        </Col>
                      )
                  )}
                </div>
              </Panel>
            </Collapse>
          ) : (
            <div>
              {customer.phone ? (
                <Col span={6} className={classes.marginB10}>
                  <div className={classes.nameContainer}>{formatPhoneNumber(customer.phone)}</div>
                  <div className={classes.subtitle}>PRIMARY PHONE</div>
                </Col>
              ) : (
                <div className={classes.noInfo}>
                  <b>No information</b>
                </div>
              )}
            </div>
          )}

          <TitleWithDivider title="List of Tasks" />
          {customer?.tasks?.length ? (
            <div>
              {customer.tasks.map(task => (
                <TaskItem key={task.id} task={task} />
              ))}
            </div>
          ) : (
            <div className={classes.noInfo}>
              <b>No tasks</b>
            </div>
          )}
        </div>
        <Space>
          <CustomButton onClick={onClose} type="primary" size="small" color="outlined" text="Close Popup" />
        </Space>
      </Spin>
    </Modal>
  );
};

export default DetailsModal;
